/**
 * NigaBond Trader — bond instrument registry.
 *
 * Covers a realistic pilot universe:
 *   - FGN benchmarks (sovereign, quasi-risk-free in NGN)
 *   - FGN Sukuk (Islamic-structured, no coupon; rental income)
 *   - State & municipal issues (Lagos, Edo, Kaduna)
 *   - Corporate issues (Dangote, MTN, Access, Fidelity)
 *   - Treasury Bills (zero-coupon, discount-quoted)
 *
 * In production this catalogue flows from the BAL's /v1/instruments/bonds
 * endpoint which mirrors the NGX XBondMarket feed with overrides for
 * Nigastock-added corporate issues. The shape here is intentionally the
 * JSON we'd receive on the wire.
 */

export type BondType = 'FGN' | 'State' | 'Municipal' | 'Corporate' | 'Sukuk' | 'TBill';
export type BondRating = 'AAA' | 'AA+' | 'AA' | 'AA-' | 'A+' | 'A' | 'A-' | 'BBB+' | 'BBB' | 'BBB-' | 'BB' | 'B' | 'NR';

export interface BondInstrument {
  code: string;              // trading code (e.g. FGN27-14.55)
  name: string;              // display name
  type: BondType;
  issuer: string;
  couponPct: number;         // annual coupon as %
  frequency: 1 | 2 | 4;      // payments/year
  issueDate: string;         // ISO
  maturityDate: string;      // ISO
  faceValueNaira: number;    // per unit face (usually 1000)
  minLotFace: number;        // minimum trade lot (naira face value)
  cleanPricePct: number;     // last quoted clean price, % of face
  rating: BondRating;
  taxExempt?: boolean;       // municipal issues may be tax-exempt on coupon
  sector?: string;
  /** Pilot seed for synthetic live quoting. */
  seedNote?: string;
}

export const BONDS: BondInstrument[] = [
  // --- FGN benchmark curve ---
  { code: 'FGN27-14.55', name: 'FGN Jul-2027 14.55%',  type: 'FGN',       issuer: 'Federal Government of Nigeria',
    couponPct: 14.55, frequency: 2, issueDate: '2023-07-25', maturityDate: '2027-07-25',
    faceValueNaira: 1000, minLotFace: 5_000_000, cleanPricePct: 98.75, rating: 'AAA' },
  { code: 'FGN29-13.53', name: 'FGN Mar-2029 13.53%',  type: 'FGN',       issuer: 'Federal Government of Nigeria',
    couponPct: 13.53, frequency: 2, issueDate: '2022-03-27', maturityDate: '2029-03-27',
    faceValueNaira: 1000, minLotFace: 5_000_000, cleanPricePct: 94.20, rating: 'AAA' },
  { code: 'FGN32-18.50', name: 'FGN Feb-2032 18.50%',  type: 'FGN',       issuer: 'Federal Government of Nigeria',
    couponPct: 18.50, frequency: 2, issueDate: '2024-02-21', maturityDate: '2032-02-21',
    faceValueNaira: 1000, minLotFace: 5_000_000, cleanPricePct: 101.40, rating: 'AAA' },
  { code: 'FGN37-16.25', name: 'FGN Apr-2037 16.25%',  type: 'FGN',       issuer: 'Federal Government of Nigeria',
    couponPct: 16.25, frequency: 2, issueDate: '2022-04-18', maturityDate: '2037-04-18',
    faceValueNaira: 1000, minLotFace: 5_000_000, cleanPricePct: 95.60, rating: 'AAA' },
  { code: 'FGN49-12.98', name: 'FGN Mar-2049 12.98%',  type: 'FGN',       issuer: 'Federal Government of Nigeria',
    couponPct: 12.98, frequency: 2, issueDate: '2019-03-27', maturityDate: '2049-03-27',
    faceValueNaira: 1000, minLotFace: 5_000_000, cleanPricePct: 82.15, rating: 'AAA' },
  // --- FGN Sukuk ---
  { code: 'FGN-SK-2030', name: 'FGN Oct-2030 Sukuk 15.75%', type: 'Sukuk', issuer: 'Federal Government of Nigeria',
    couponPct: 15.75, frequency: 2, issueDate: '2023-10-21', maturityDate: '2030-10-21',
    faceValueNaira: 1000, minLotFace: 1_000_000, cleanPricePct: 99.10, rating: 'AAA', sector: 'Sovereign Sukuk' },
  // --- Treasury Bills (zero-coupon, discount-quoted) ---
  { code: 'NTB-091D-26.11', name: '91-Day T-Bill (Nov 2026 mat)', type: 'TBill', issuer: 'Debt Management Office',
    couponPct: 0, frequency: 2, issueDate: '2026-08-12', maturityDate: '2026-11-12',
    faceValueNaira: 1000, minLotFace: 1_000_000, cleanPricePct: 97.80, rating: 'AAA' },
  { code: 'NTB-182D-27.02', name: '182-Day T-Bill (Feb 2027 mat)', type: 'TBill', issuer: 'Debt Management Office',
    couponPct: 0, frequency: 2, issueDate: '2026-08-12', maturityDate: '2027-02-12',
    faceValueNaira: 1000, minLotFace: 1_000_000, cleanPricePct: 95.20, rating: 'AAA' },
  { code: 'NTB-364D-27.08', name: '364-Day T-Bill (Aug 2027 mat)', type: 'TBill', issuer: 'Debt Management Office',
    couponPct: 0, frequency: 2, issueDate: '2026-08-12', maturityDate: '2027-08-11',
    faceValueNaira: 1000, minLotFace: 1_000_000, cleanPricePct: 88.90, rating: 'AAA' },
  // --- Municipal / State bonds ---
  { code: 'LAGOS29-14.00', name: 'Lagos State Nov-2029 14.00%', type: 'State',     issuer: 'Lagos State Government',
    couponPct: 14.00, frequency: 2, issueDate: '2022-11-18', maturityDate: '2029-11-18',
    faceValueNaira: 1000, minLotFace: 100_000, cleanPricePct: 92.30, rating: 'AA+', taxExempt: true, sector: 'Sub-sovereign' },
  { code: 'EDO31-12.80',   name: 'Edo State Jun-2031 12.80%',   type: 'State',     issuer: 'Edo State Government',
    couponPct: 12.80, frequency: 2, issueDate: '2024-06-30', maturityDate: '2031-06-30',
    faceValueNaira: 1000, minLotFace: 100_000, cleanPricePct: 88.40, rating: 'AA-', taxExempt: true, sector: 'Sub-sovereign' },
  { code: 'KADUNA30-15.50',name: 'Kaduna State Aug-2030 15.50%', type: 'Municipal', issuer: 'Kaduna State Government',
    couponPct: 15.50, frequency: 2, issueDate: '2023-08-15', maturityDate: '2030-08-15',
    faceValueNaira: 1000, minLotFace: 100_000, cleanPricePct: 95.80, rating: 'AA-', taxExempt: true, sector: 'Sub-sovereign' },
  // --- Corporate ---
  { code: 'DANG-28-13.80', name: 'Dangote Cement Apr-2028 13.80%', type: 'Corporate', issuer: 'Dangote Cement Plc',
    couponPct: 13.80, frequency: 2, issueDate: '2023-04-14', maturityDate: '2028-04-14',
    faceValueNaira: 1000, minLotFace: 100_000, cleanPricePct: 97.20, rating: 'AA', sector: 'Materials' },
  { code: 'MTNN-29-14.30', name: 'MTN Nigeria May-2029 14.30%',    type: 'Corporate', issuer: 'MTN Nigeria Plc',
    couponPct: 14.30, frequency: 2, issueDate: '2024-05-21', maturityDate: '2029-05-21',
    faceValueNaira: 1000, minLotFace: 100_000, cleanPricePct: 96.10, rating: 'AA', sector: 'Telecom' },
  { code: 'ACCESS-31-16.75', name: 'Access Bank Jan-2031 16.75%',  type: 'Corporate', issuer: 'Access Bank Plc',
    couponPct: 16.75, frequency: 2, issueDate: '2024-01-19', maturityDate: '2031-01-19',
    faceValueNaira: 1000, minLotFace: 100_000, cleanPricePct: 99.40, rating: 'A+', sector: 'Financials' },
  { code: 'FIDELITY-30-15.00', name: 'Fidelity Bank Jul-2030 15.00%', type: 'Corporate', issuer: 'Fidelity Bank Plc',
    couponPct: 15.00, frequency: 2, issueDate: '2023-07-12', maturityDate: '2030-07-12',
    faceValueNaira: 1000, minLotFace: 100_000, cleanPricePct: 93.20, rating: 'A', sector: 'Financials' },
];

export function bondByCode(code: string): BondInstrument | undefined {
  return BONDS.find((b) => b.code === code);
}

/** Deterministic live-quote nudge so prices "breathe" in the offline demo. */
export function nudgedCleanPrice(base: number, symbol: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < symbol.length; i++) h = Math.imul(h ^ symbol.charCodeAt(i), 16777619) >>> 0;
  const delta = ((h % 1000) / 1000) - 0.5; // -0.5..+0.5
  return +(base + delta * 0.35).toFixed(2);
}
